import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import type {
  PMWStockBackendAiAnimatedSticker,
  PMWStockBackendAiIcon,
  PMWStockBackendAiSticker,
  PMWStockBackendAnimatedSticker,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {GridAnimatedStickerItemStorage} from '@Components/base-grid/components/grid-animated-sticker-item';
import type {GridItemBaseStorage, GridItemStorage, GridStockMediaItemBackendData} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {isMobile} from 'react-device-detect';
import {v4 as uuidv4} from 'uuid';
import type {GridStockAudioItemBackEndData, GridStockAudioItemStorage} from '@Components/base-grid/components/grid-stock-audio-item';
import type {GridAiImageItemStorage, GridStockImageItemBackEndData, GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import {
  gettyImageUploadV2,
  gettyVideoUploadV2,
  pixabayImageUploadV2,
  pixabayVideoUploadV2,
  SOURCE,
  storyBlocksAudioUploadV2,
  storyBlocksImageUploadV2,
  storyBlocksVideoUploadV2,
} from '@Libraries/stock-graphic-library';
import {runUserAudioUploadJob, runUserImageUploadJob, runUserVideoUploadJob} from '@Libraries/user-media-library';
import {preFillAiPromptBox} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pre-fill.library';
import {AI_IMAGE_PROMPT_BOX_ID} from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box.types';
import {AI_VIDEO_PROMPT_BOX_ID} from '@Panels/ai-video-panel/ai-video-panel.types';
import type {AITextToImageBackendData, AudioCategoryBackendData, StockGetterOptions} from '@Libraries/pmw-stock-media-library';
import {getPMWStockImageThumbURL, StockSources} from '@Libraries/pmw-stock-media-library';
import type {SeasonName} from '@Libraries/seasons.library';
import {getActiveStockSeason} from '@Libraries/seasons.library';
import {StickerItemSource} from '@Libraries/pmw-stock-media-library.types';
import type {GridStockVideoItemBackEndData, GridStockVideoItemStorage} from '@Components/base-grid/components/grid-stock-video-item';
import type {GridShapeItemStorage} from '@Components/base-grid/components/grid-shape-item';
import type {GridClipartItemBackEndData, GridClipartItemStorage} from '@Components/base-grid/components/grid-clipart-item';
import type {
  AiCarouselItemMeta,
  GridItemBaseStorageBackendData,
  SelectedFilter,
  StockFilter,
  StockFilterID,
  StockItemDelivery,
  StockSelectionUpload,
  StockSelectionUploadResult,
  StockSubFilterRef,
  UploadStockSelectionParams,
} from '@Panels/stock-panel/stock-panel.types';
import type {AiPanelKey} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button.types';
import {AiMenuOpenSource, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {
  AiCarouselContentType,
  AUDIO_CATEGORY_ID_TO_STOCK_BUTTON_FILTER,
  ICON_FILTERS_TO_CATEGORY_MAPPING,
  SHAPE_FILTERS_TO_CATEGORY_MAPPING,
  MUTUALLY_EXCLUSIVE_SUBFILTERS,
  AnimationSubFilter,
  StockButtonFilters,
  StockCarouselType,
  StockFilterTypes,
  StockGridIDs,
  StockPanelTabs,
  UHD_APPLIED_VIDEO_FILTER_ID,
  CAROUSEL_ITEM_HEIGHT,
  DEFAULT_APPLIED_SUBFILTER,
  FAVORITES_EMPTY_MESSAGE_KEY_BY_TAB,
} from '@Panels/stock-panel/stock-panel.types';
import type {StockFavoriteSource} from '@Panels/stock-panel/stock-favorites.types';
import {FAVORITES_COMPATIBLE_FILTERS} from '@Panels/stock-panel/stock-favorites.types';
import type {AiVideoBackendData} from '@Libraries/ai-video.library';
import {getAiVideoThumbUrl, uploadGeneratedAiVideo} from '@Libraries/ai-video.library';
import {addGeneratedVoiceToDesign} from '@Libraries/text-to-speech.library';
import type {
  AiAnimatedStickerData,
  AnimatedStickerData,
  AudioData,
  ElementData,
  ImageData,
  PMWAiIconData,
  PMWAiStickerData,
  PMWExtractedGettyStickerData,
  PMWIconData,
  PMWShapeData,
  PMWStillStickerData,
  TempImageData,
  VideoData,
} from '@Libraries/add-media-library';
import {
  ElementDataType,
  getAudioDataFromUserAudioVO,
  getGeneratedAiImageDataFromGridItemStorage,
  getImageDataForPMWStockImage,
  getImageDataFromUserImageVO,
  getStockImageDataFromGridStockImageItemStorage,
  getVideoDataFromUserVideoVO,
} from '@Libraries/add-media-library';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import type {TabItemContentStorage} from '@Components/tabs';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {getHashedFilenameForStockAsset} from '@Libraries/user-image-library';
import type {UserVideoVOResponse} from '@Libraries/user-video-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {isPremiumUser} from '@Utils/user.util';
import type {GridMediaItemTag} from '@Components/base-grid/components/grid-media-item';
import {GridMediaItemTagType} from '@Components/base-grid/components/grid-media-item';
import {getUrlForGeneratedAiImage, isAiEditedImage, isAiTextToImageBackendData, textToImageUpload} from '@Libraries/text-to-image-library';
import {repoImageScreenURL, repoImageThumbURL} from '@Libraries/graphic-item-image-library';
import {capitalize} from '@Utils/string.util';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import type {AnimatedSprite} from '@Libraries/animated-sprite.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {getUrlForImageItemScreenTier, ScreenTier} from '@Libraries/image-item.library';
import {imageUrlToDataUrlOrNull} from '@Utils/image.util';
import {doesPublicFileExistAsync} from '@Utils/file.util';
import type {GridExtractedGettyStickerItemStorage} from '@Components/base-grid/components/grid-extracted-getty-sticker-item';
import type {
  GridAiIconItemBackEndData,
  GridAiIconItemStorage,
  GridIconItemBackEndData,
  GridIconItemStorage,
} from '@Components/base-grid/components/grid-icon-item/grid-icon-item.types';
import type {
  GridAiStickerItemBackEndData,
  GridAiStickerItemStorage,
  GridStillStickerItemBackEndData,
  GridStillStickerItemStorage,
} from '@Components/base-grid/components/grid-still-sticker-item/grid-still-sticker-item.types';
import type {CanvasDropPosition} from '@Libraries/canvas-item-drag.library';
import {placeItemAtDropPosition} from '@Libraries/canvas-item-drag.library';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import {showLoading, updateLoadingText} from '@Libraries/loading-toast-library';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';

const FALLBACK_GRID_ITEM_WIDTH = 170;
const FALLBACK_GRID_ITEM_HEIGHT = 170;
const STOCK_AUDIO_GRID_ITEM_WIDTH = 220;

export const getAiSkeletonItem = (id: string): GridSkeletonItemStorage => {
  return {
    id: id,
    type: GRID_ITEM_TYPE.SKELETON,
    width: 180,
    height: 70,
    useFixedDimensions: false,
  };
};

const ANIMATED_STICKER_SOURCES = [StockSources.PMW_ANIMATED_STICKER, StockSources.PMW_AI_ANIMATED_STICKER];

export const getAnimatedStickerSources = (): StockSources[] => {
  return ANIMATED_STICKER_SOURCES;
};

const RECENT_ITEM_TYPE_TO_TAB: Partial<Record<GRID_ITEM_TYPE, StockPanelTabs>> = {
  [GRID_ITEM_TYPE.STOCK_IMAGE]: StockPanelTabs.PHOTOS,
  [GRID_ITEM_TYPE.IMAGE]: StockPanelTabs.PHOTOS,
  [GRID_ITEM_TYPE.USER_IMAGE]: StockPanelTabs.PHOTOS,
  [GRID_ITEM_TYPE.STOCK_VIDEO]: StockPanelTabs.VIDEOS,
  [GRID_ITEM_TYPE.VIDEO]: StockPanelTabs.VIDEOS,
  [GRID_ITEM_TYPE.USER_VIDEO]: StockPanelTabs.VIDEOS,
  [GRID_ITEM_TYPE.STOCK_AUDIO]: StockPanelTabs.AUDIOS,
  [GRID_ITEM_TYPE.AUDIO]: StockPanelTabs.AUDIOS,
  [GRID_ITEM_TYPE.USER_AUDIO]: StockPanelTabs.AUDIOS,
  [GRID_ITEM_TYPE.PMW_STOCK_SHAPE]: StockPanelTabs.SHAPES,
  [GRID_ITEM_TYPE.SHAPE]: StockPanelTabs.SHAPES,
  [GRID_ITEM_TYPE.PMW_STOCK_ICON]: StockPanelTabs.ICONS,
  [GRID_ITEM_TYPE.ICON]: StockPanelTabs.ICONS,
  [GRID_ITEM_TYPE.PMW_AI_ICON]: StockPanelTabs.ICONS,
  [GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.STILL_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.ANIMATED_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_AI_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.CLIPART]: StockPanelTabs.STICKERS,
};

// Only the single-source item types are listed — these are the ones that can appear in a blacklistedSources list
// (e.g. the slideshow blacklists animated stickers). Image/video/audio span several providers, so they have no single
// source here and are never blacklisted in practice.
export const PMW_ITEM_TYPE_TO_STOCK_SOURCE: Partial<Record<GRID_ITEM_TYPE, StockFavoriteSource>> = {
  [GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER]: StockSources.PMW_ANIMATED_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER]: StockSources.PMW_AI_ANIMATED_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER]: StockSources.PMW_STILL_STICKER,
  [GRID_ITEM_TYPE.PMW_AI_STICKER]: StockSources.PMW_AI_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER]: StockSources.PMW_EXTRACTED_GETTY_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_ICON]: StockSources.PMW_ICON,
  [GRID_ITEM_TYPE.PMW_AI_ICON]: StockSources.PMW_AI_ICON,
  [GRID_ITEM_TYPE.PMW_STOCK_SHAPE]: StockSources.PMW_SHAPE,
};

/**
 * A recent stock item belongs in this panel's Recents carousel only when its media type is one the panel shows
 * (tabsToShow) and its source is not blacklisted (e.g. animated stickers in the slideshow flow).
 */
export const isRecentStockItemAllowed = (item: GridItemStorage, tabsToShow: StockPanelTabs[], blacklistedSources: StockSources[]): boolean => {
  const tab = RECENT_ITEM_TYPE_TO_TAB[item.type];
  if (!tab || !tabsToShow.includes(tab)) {
    return false;
  }
  const source = PMW_ITEM_TYPE_TO_STOCK_SOURCE[item.type];
  return !(source && blacklistedSources.includes(source));
};

export const animatedStickerDataToAnimatedStickerItem = (animatedStickerData: PMWStockBackendAnimatedSticker): GridAnimatedStickerItemStorage => {
  return {
    hashedFilename: animatedStickerData.hashedFilename,
    type: GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER,
    id: animatedStickerData.idPmwStockMedia,
    frameRate: Number(animatedStickerData.frameRate),
    duration: Number(animatedStickerData.duration),
    width: animatedStickerData.width !== 0 ? animatedStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: animatedStickerData.height !== 0 ? animatedStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
    highResAnimatedSprite: animatedStickerData.highResAnimatedSprite,
    screenAnimatedSprite: animatedStickerData.screenAnimatedSprite,
  };
};

export const aiAnimatedStickerDataToAiAnimatedStickerItem = (stickerData: PMWStockBackendAiAnimatedSticker): GridAnimatedStickerItemStorage => {
  return {
    hashedFilename: stickerData.hashedFilename,
    type: GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER,
    id: stickerData.idPmwStockMedia,
    frameRate: Number(stickerData.frameRate),
    duration: Number(stickerData.duration),
    width: stickerData.width !== 0 ? stickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: stickerData.height !== 0 ? stickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
    highResAnimatedSprite: stickerData.highResAnimatedSprite,
    screenAnimatedSprite: stickerData.screenAnimatedSprite,
  };
};

export const audioDataToAudioItem = (audioData: GridStockAudioItemBackEndData): GridStockAudioItemStorage => {
  return {
    id: audioData.id as string,
    type: GRID_ITEM_TYPE.STOCK_AUDIO,
    name: audioData.t as string,
    audioSrc: audioData.up as string,
    imgThumbSrc: audioData.u as string,
    duration: audioData.cl as string,
    source: SOURCE.STORYBLOCKS,
    data: {
      id: audioData.id as string,
      mood: audioData.m as string,
      genre: audioData.g as string,
      categoryIds: getAudioCategoryIds(audioData.categoryIds),
    },
  };
};

/** A track's Storyblocks category ids, the ids the audio pills filter by, kept so that a saved track can be matched against the pills. */
const getAudioCategoryIds = (categoryIds: unknown): number[] => {
  return Array.isArray(categoryIds) ? categoryIds.filter((categoryId): categoryId is number => typeof categoryId === 'number') : [];
};

export const clipartDataToClipartItem = (clipartData: GridClipartItemBackEndData, source: StockSources): GridClipartItemStorage => {
  let clipartSource: SOURCE;
  let data: Record<string, any>;
  let clipartThumbURL;

  switch (source) {
    case StockSources.GETTY_ILLUSTRATION:
      clipartThumbURL = clipartData.u;
      clipartSource = SOURCE.GETTY_ILLUSTRATIONS;
      data = {
        id: clipartData.id ?? uuidv4(),
        artist: clipartData.a as string,
        title: clipartData.t as string,
        col: clipartData.c as string,
        type: clipartData.ty as string,
        tag: getTagForGettyMedia(),
      };
      break;
    case StockSources.PIXABY_ILLUSTRATION:
      clipartThumbURL = clipartData.u;
      clipartSource = SOURCE.PIXABAY_ILLUSTRATIONS;
      data = {
        id: clipartData.id ?? uuidv4(),
        hu: clipartData.hu as string,
      };
      break;
    case StockSources.STORYBLOCK_ILLUSTRATION:
      clipartThumbURL = clipartData.tu;
      clipartSource = SOURCE.STORYBLOCKS_ILLUSTRATIONS;
      data = {
        id: clipartData.id ?? uuidv4(),
        title: clipartData.t as string,
        format: clipartData.defaultFormat as string,
      };
      break;
    default:
      throw new Error(`Invalid clipart source passed: ${source}`);
  }

  return {
    type: GRID_ITEM_TYPE.CLIPART,
    source: clipartSource,
    id: clipartData.id ?? uuidv4(),
    thumbSrc: clipartThumbURL,
    data,
    width: clipartData.w as number,
    height: clipartData.h as number,
    tag: source === StockSources.GETTY_ILLUSTRATION ? getTagForGettyMedia() : undefined,
    hidden: clipartData.hidden ?? undefined,
  };
};

export const getTagForGettyMedia = (): Partial<GridMediaItemTag> => {
  if (isPremiumUser()) {
    return {
      type: GridMediaItemTagType.PREMIUM_SOURCE,
    };
  }
  return {
    type: GridMediaItemTagType.SOURCE,
  };
};

/**
 * Getty-licensed content spans three grid-item shapes: Getty photos/videos (source GETTY), Getty sticker
 * sheets on the stickers tab (CLIPART items with source GETTY_ILLUSTRATIONS), and extracted Getty stickers
 * (identified by item type only — they carry no source). All of them require the Getty license agreement.
 */
export const isGettyLicensedGridItem = (item: GridItemStorage): boolean => {
  return item.source === SOURCE.GETTY || item.source === SOURCE.GETTY_ILLUSTRATIONS || item.type === GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER;
};

export const iconDataToIconItem = (iconData: GridIconItemBackEndData): GridIconItemStorage => {
  return {
    id: iconData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_STOCK_ICON,
    hashedFilename: iconData.hashedFilename,
  };
};

export const aiIconDataToAiIconItem = (aiIconData: GridAiIconItemBackEndData): GridAiIconItemStorage => {
  return {
    id: aiIconData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_AI_ICON,
    hashedFilename: aiIconData.hashedFilename,
  };
};

export const stillStickerDataToStickerItem = (stillStickerData: GridStillStickerItemBackEndData): GridStillStickerItemStorage => {
  return {
    id: stillStickerData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER,
    hashedFilename: stillStickerData.hashedFilename,
    width: stillStickerData.width !== 0 ? stillStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: stillStickerData.height !== 0 ? stillStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
  };
};

export const aiStickerDataToAiStickerItem = (aiStickerData: GridAiStickerItemBackEndData): GridAiStickerItemStorage => {
  return {
    id: aiStickerData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_AI_STICKER,
    hashedFilename: aiStickerData.hashedFilename,
    width: aiStickerData.width !== 0 ? aiStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: aiStickerData.height !== 0 ? aiStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
  };
};

export const extractedGettyStickerDataToStickerItem = (extractedGettyStickerData: GridStockMediaItemBackendData): GridExtractedGettyStickerItemStorage => {
  return {
    id: extractedGettyStickerData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER,
    hashedFilename: extractedGettyStickerData.hashedFilename,
    width: extractedGettyStickerData.width !== 0 ? extractedGettyStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: extractedGettyStickerData.height !== 0 ? extractedGettyStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
    tag: getTagForGettyMedia(),
  };
};

export const imageDataToImageItem = (backEndData: GridStockImageItemBackEndData, source: StockSources): GridStockImageItemStorage => {
  let thumbSrc;
  let id;
  let data = {};
  let src: SOURCE;

  switch (source) {
    case StockSources.GETTY_IMAGE:
      src = SOURCE.GETTY;
      thumbSrc = backEndData.u as string;
      id = backEndData.id as string;
      data = {
        id: backEndData.id as string,
        artist: backEndData.a as string,
        title: backEndData.t as string,
        col: backEndData.c as number,
        type: backEndData.ty as string,
      };
      break;

    case StockSources.PIXABY_IMAGE:
      src = SOURCE.PIXABAY;
      thumbSrc = backEndData.u as string;
      id = backEndData.id as string;
      data = {
        hu: backEndData.hu as string,
      };
      break;

    case StockSources.STORYBLOCK_IMAGE:
      src = SOURCE.STORYBLOCKS;
      thumbSrc = backEndData.tu as string;
      id = backEndData.id as string;
      data = {
        id: backEndData.id as string,
        title: backEndData.t as string,
        format: backEndData.defaultFormat as string,
      };
      break;

    case StockSources.PMW_STOCK_IMAGE:
      src = SOURCE.PMW_IMAGE;
      thumbSrc = getPMWStockImageThumbURL(backEndData.hashedFilename as string);
      id = backEndData.idPmwStockMedia as string;
      data = {
        hashedFilename: backEndData.hashedFilename as string,
        hasTransparency: !!backEndData.has_transparency,
      };
      break;

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return {
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    id,
    thumbSrc,
    source: src,
    data,
    width: backEndData.w as number,
    height: backEndData.h as number,
    tag: source === StockSources.GETTY_IMAGE ? getTagForGettyMedia() : undefined,
  };
};

export const videoDataToVideoItem = (backEndData: GridStockVideoItemBackEndData, source: StockSources): GridStockVideoItemStorage | undefined => {
  if (source && backEndData.u && backEndData.up) {
    switch (source) {
      case StockSources.GETTY_VIDEO:
        return {
          id: backEndData.id as string,
          type: GRID_ITEM_TYPE.STOCK_VIDEO,
          source: SOURCE.GETTY,
          imgThumbSrc: backEndData.u as string,
          videoThumbSrc: backEndData.up as string,
          isUHD: !!backEndData.UHD,
          duration: backEndData.cl as string,
          data: {
            id: backEndData.id as string,
            artist: backEndData.a as string,
            title: backEndData.t as string,
            col: backEndData.c as number,
          },
          width: backEndData.w as number,
          height: backEndData.h as number,
          tag: getTagForGettyMedia(),
        };

      case StockSources.PIXABY_VIDEO:
        return {
          id: backEndData.id as string,
          type: GRID_ITEM_TYPE.STOCK_VIDEO,
          source: SOURCE.PIXABAY,
          imgThumbSrc: backEndData.u as string,
          videoThumbSrc: backEndData.up as string,
          isUHD: false, // pixabay doesn't provide 4k videos
          duration: backEndData.cl as string,
          data: {
            id: backEndData.id as string,
            highResSrc: backEndData.hr as string,
            smallResSrc: backEndData.sr as string,
          },
          width: backEndData.w as number,
          height: backEndData.h as number,
        };

      case StockSources.STORYBLOCK_VIDEO:
        return {
          id: backEndData.id as string,
          type: GRID_ITEM_TYPE.STOCK_VIDEO,
          source: SOURCE.STORYBLOCKS,
          imgThumbSrc: backEndData.u as string,
          videoThumbSrc: backEndData.up as string,
          isUHD: !!backEndData.UHD,
          duration: backEndData.cl as string,
          data: {
            id: backEndData.id as string,
            title: backEndData.t as string,
          },
          width: backEndData.w as number,
          height: backEndData.h as number,
        };

      default:
        throw new Error(`Unhandled source: ${source}`);
    }
  }
  return undefined;
};

export const shapeDataToShapeItem = (shapeData: PMWStockBackendShape): GridShapeItemStorage => {
  return {
    hashedFilename: shapeData.idPMWStockShape,
    type: GRID_ITEM_TYPE.PMW_STOCK_SHAPE,
    id: shapeData.idPmwStockMedia,
  };
};

const handleImageTypeUpload = async (gridItem: GridStockImageItemStorage, shouldLoadDataUrlImage = false): Promise<ElementData> => {
  switch (gridItem.source) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case SOURCE.PMW_IMAGE:
      return getImageDataForPMWStockImage(gridItem);
    default:
      return doUploadStockImageGridItem(gridItem, shouldLoadDataUrlImage);
  }
};

export const uploadSelectedItem = async (gridItem: GridItemStorage, shouldLoadDataUrlImage = false): Promise<ElementData> => {
  switch (gridItem.type) {
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return handleImageTypeUpload(gridItem as GridStockImageItemStorage, shouldLoadDataUrlImage);
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return uploadStockVideoGridItem(gridItem as GridStockVideoItemStorage);
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return uploadStockAudioGridItem(gridItem as GridStockAudioItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
      return uploadIconGridItem(gridItem as GridIconItemStorage);
    case GRID_ITEM_TYPE.PMW_AI_ICON:
      return uploadAiIconGridItem(gridItem as GridAiIconItemStorage);
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return uploadAiStickerGridItem(gridItem as GridAiStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
      return uploadStillStickerGridItem(gridItem as GridStillStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER:
      return uploadExtractedGettyStickerGridItem(gridItem as GridExtractedGettyStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
      return uploadShapeGridItem(gridItem as GridShapeItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
      return uploadAnimatedStickerGridItem(gridItem as GridAnimatedStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return uploadAiAnimatedStickerGridItem(gridItem as GridAnimatedStickerItemStorage);
    case GRID_ITEM_TYPE.CLIPART:
      return uploadClipartGridItem(gridItem as GridClipartItemStorage);
    default:
      throw new Error(`Unhandled grid item: ${gridItem.type}`);
  }
};

const doUploadStockImageGridItem = async (gridItem: GridItemStorage, shouldLoadDataUrlImage = false): Promise<ElementData> => {
  if (isAiEditedImageGridItem(gridItem)) {
    return getImageDataFromUserImageVO(gridItem.backendData.userImage);
  }
  if (shouldLoadDataUrlImage) {
    let itemToAddData;
    let doesFileExist;
    if (gridItem.source === SOURCE.AI_TEXT_TO_IMAGE) {
      itemToAddData = getGeneratedAiImageDataFromGridItemStorage(gridItem as GridStockImageItemStorage);
      const userImageHashedFilename = await getUserImageHashedFilenameForAiImage(Number(gridItem.id));
      doesFileExist =
        !!userImageHashedFilename && (await doesPublicFileExistAsync(getUrlForImageItemScreenTier(userImageHashedFilename, itemToAddData.source, ScreenTier.SMALL, false)));
    } else {
      itemToAddData = getStockImageDataFromGridStockImageItemStorage(gridItem as GridStockImageItemStorage);
      doesFileExist = await doesPublicFileExistAsync(getUrlForImageItemScreenTier(itemToAddData.hashedFilename, itemToAddData.source, ScreenTier.SMALL, false));
    }
    if (!doesFileExist) {
      const tempUploadingImageUID = uuidv4();

      const imageURL = gridItem.source === SOURCE.AI_TEXT_TO_IMAGE ? (gridItem.highResSrc as string) : (gridItem.thumbSrc as string);
      const dataurl = await imageUrlToDataUrlOrNull(imageURL);
      if (dataurl !== null) {
        void uploadStockImageGridItem(gridItem as GridStockImageItemStorage, tempUploadingImageUID);

        return {
          uid: tempUploadingImageUID,
          dataUrl: dataurl,
          type: itemToAddData.type,
          source: itemToAddData.source,
          stockId: gridItem.source === SOURCE.AI_TEXT_TO_IMAGE ? undefined : gridItem.id,
        } as TempImageData;
      }
      return uploadStockImageGridItem(gridItem as GridStockImageItemStorage);
    } else if (doesFileExist && gridItem.source !== SOURCE.AI_TEXT_TO_IMAGE) {
      try {
        const data = (await window.PMW.readLocal('posterimage/getUserImage', {
          hashedId: getHashedFilenameForStockAsset(gridItem.source as SOURCE, gridItem.id),
        })) as UserImageVOResponse;
        return getImageDataFromUserImageVO(data);
      } catch {
        return uploadStockImageGridItem(gridItem as GridStockImageItemStorage);
      }
    }
  }
  return uploadStockImageGridItem(gridItem as GridStockImageItemStorage);
};

export const getUserImageHashedFilenameForAiImage = async (aiGeneratedImageId: number): Promise<string> => {
  return (await window.PMW.readLocal('stockmedia/getUserImageHashedFilenameForAiImage', {
    aiGeneratedImageId,
  })) as Promise<string>;
};

const uploadStockImageGridItem = async (gridItem: GridStockImageItemStorage, tempUploadingImageUID?: string): Promise<ImageData> => {
  const {source} = gridItem;
  let data: UserImageVOResponse;

  switch (source as SOURCE) {
    case SOURCE.GETTY:
      data = await runUserImageUploadJob(() =>
        gettyImageUploadV2({
          imageId: gridItem.data.id as string,
          type: gridItem.data.type as string,
          title: gridItem.data.title as string,
          artist: gridItem.data.artist as string,
          collection: gridItem.data.col as number,
          tempUploadingImageUID,
        })
      );
      break;

    case SOURCE.PIXABAY:
      data = await runUserImageUploadJob(() =>
        pixabayImageUploadV2({
          url: gridItem.data.hu as string,
          ...(gridItem.id ? {imageId: gridItem.id} : {}),
          tempUploadingImageUID,
        })
      );
      break;

    case SOURCE.STORYBLOCKS:
      data = await runUserImageUploadJob(() =>
        storyBlocksImageUploadV2({
          imageId: gridItem.data.id as string,
          tempUploadingImageUID,
        })
      );
      break;

    case SOURCE.AI_TEXT_TO_IMAGE:
      data = (await window.PMW.pollingAjaxCallAsync(
        textToImageUpload.bind(null, {
          userId: gridItem.data.uploaderId ? Number(gridItem.data.uploaderId) : undefined,
          aiGeneratedImageId: gridItem.data.id as number,
          tempUploadingImageUID,
        }),
        checkGeneratedImageUploadStatus
      )) as UserImageVOResponse;
      break;

    case SOURCE.PMW_IMAGE:
      return getImageDataForPMWStockImage(gridItem);

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return getImageDataFromUserImageVO(data);
};

const checkGeneratedImageUploadStatus = async (aiGeneratedImageId: number): Promise<any> => {
  return window.PMW.readLocal('stockmedia/getGeneratedImageStatus', {
    aiGeneratedImageId,
  });
};

const uploadStockVideoGridItem = async (gridItem: GridStockVideoItemStorage): Promise<VideoData> => {
  const {source} = gridItem;
  let data: UserVideoVOResponse;

  switch (source as SOURCE) {
    case SOURCE.GETTY:
      data = await runUserVideoUploadJob(() =>
        gettyVideoUploadV2({
          videoId: gridItem.data.id as string,
          title: gridItem.data.title as string,
          artist: gridItem.data.artist as string,
          collection: gridItem.data.col as number,
        })
      );
      break;

    case SOURCE.PIXABAY:
      data = await runUserVideoUploadJob(() =>
        pixabayVideoUploadV2({
          videoId: gridItem.data.id as string,
          videoHighRes: gridItem.data.highResSrc as string,
          videoSmallRes: gridItem.data.smallResSrc as string,
        })
      );
      break;

    case SOURCE.STORYBLOCKS:
      data = await runUserVideoUploadJob(() =>
        storyBlocksVideoUploadV2({
          videoId: gridItem.data.id as string,
        })
      );
      break;

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return getVideoDataFromUserVideoVO(data);
};

const uploadStockAudioGridItem = async (gridItem: GridStockAudioItemStorage): Promise<AudioData> => {
  const data = await runUserAudioUploadJob(() =>
    storyBlocksAudioUploadV2({
      audioId: gridItem.data.id as string,
      title: gridItem.name,
    })
  );

  return getAudioDataFromUserAudioVO(data);
};

const uploadIconGridItem = (gridItem: GridIconItemStorage): PMWIconData => {
  return {
    type: ElementDataType.ICON,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadAiIconGridItem = (gridItem: GridAiIconItemStorage): PMWAiIconData => {
  return {
    type: ElementDataType.AI_ICON,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadAiStickerGridItem = (gridItem: GridAiStickerItemStorage): PMWAiStickerData => {
  return {
    type: ElementDataType.AI_STICKER,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadStillStickerGridItem = (gridItem: GridStillStickerItemStorage): PMWStillStickerData => {
  return {
    type: ElementDataType.STILL_STICKER,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadExtractedGettyStickerGridItem = (gridItem: GridExtractedGettyStickerItemStorage): PMWExtractedGettyStickerData => {
  return {
    type: ElementDataType.EXTRACTED_GETTY_STICKER,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadShapeGridItem = (gridItem: GridShapeItemStorage): PMWShapeData => {
  return {
    type: ElementDataType.PMW_SHAPE,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadAnimatedStickerGridItem = (gridItem: GridAnimatedStickerItemStorage): AnimatedStickerData => {
  return {
    type: ElementDataType.ANIMATED_STICKER,
    hashedFilename: gridItem.hashedFilename,
    frameRate: gridItem.frameRate,
    duration: gridItem.duration,
    highResAnimatedSprite: gridItem.highResAnimatedSprite,
    screenAnimatedSprite: gridItem.screenAnimatedSprite,
    stickerSource: StickerItemSource.PMW_ANIMATED_STICKER,
  };
};

const uploadAiAnimatedStickerGridItem = (gridItem: GridAnimatedStickerItemStorage): AiAnimatedStickerData => {
  return {
    type: ElementDataType.AI_ANIMATED_STICKER,
    hashedFilename: gridItem.hashedFilename,
    frameRate: gridItem.frameRate,
    duration: gridItem.duration,
    highResAnimatedSprite: gridItem.highResAnimatedSprite,
    screenAnimatedSprite: gridItem.screenAnimatedSprite,
    stickerSource: StickerItemSource.PMW_AI_ANIMATED_STICKER,
  };
};

// The tabs whose catalogue actually carries seasonal content, and so show the seasonal pill.
// Shapes and Icons are deliberately absent: they have none, which is why the in-season browse
// demotes their carousels (see getSeasonalBrowseTabOrder).
const SEASONAL_PILL_TABS = [StockPanelTabs.PHOTOS, StockPanelTabs.STICKERS, StockPanelTabs.VIDEOS, StockPanelTabs.AUDIOS];

/**
 * The filters a tab opens with. The seasonal pill starts on for a browse — a user who has asked
 * for nothing in particular gets the season's picks — and off for a search, where the user's own
 * words own the results until they turn the season back on themselves.
 */
export const getDefaultSelectedFilterForTab = (tab: StockPanelTabs, searchTerm: string | undefined): SelectedFilter[] => {
  switch (tab) {
    case StockPanelTabs.VIDEOS:
    case StockPanelTabs.PHOTOS:
    case StockPanelTabs.STICKERS:
    case StockPanelTabs.AUDIOS:
      return getDefaultSeasonalFilterForTab(tab, searchTerm);
    case StockPanelTabs.ALL:
    case StockPanelTabs.ICONS:
    case StockPanelTabs.TEXT_TO_IMAGE:
    case StockPanelTabs.SHAPES:
    case StockPanelTabs.FAVORITES:
      return [];
    default:
      throw new Error(`Unhandled tab: ${tab}`);
  }
};

const getDefaultSeasonalFilterForTab = (tab: StockPanelTabs, searchTerm: string | undefined): SelectedFilter[] => {
  const season = getActiveStockSeason();
  if (season === null || !SEASONAL_PILL_TABS.includes(tab) || (searchTerm ?? '').trim() !== '') {
    return [];
  }

  return [{filter: StockButtonFilters.SEASONAL, subFilter: season}];
};

/**
 * The filter list with the seasonal pill reset to its default for the current search state — on
 * while browsing, off once the user has typed — leaving every other filter as the user set it.
 * Toggling the pill by hand does not run this, so a deliberate choice survives until the search
 * itself changes.
 */
export const withSeasonalFilterResetForSearch = (selectedFilters: SelectedFilter[], tab: StockPanelTabs, searchTerm: string | undefined): SelectedFilter[] => {
  const withoutSeasonal = selectedFilters.filter((selectedFilter) => {
    return selectedFilter.filter !== StockButtonFilters.SEASONAL.valueOf();
  });

  return [...getDefaultSeasonalFilterForTab(tab, searchTerm), ...withoutSeasonal];
};

/** Whether the user has picked an option of any of these pills; a pill left on "All" or switched off has none. */
export const isAnySubFilterSelected = (selectedFilters: SelectedFilter[], filterIDs: readonly string[]): boolean => {
  return selectedFilters.some((selectedFilter) => selectedFilter.subFilter !== null && filterIDs.includes(selectedFilter.filter));
};

export const isFavoritesFilterSelected = (selectedFilters: SelectedFilter[]): boolean => {
  return isAnySubFilterSelected(selectedFilters, [StockButtonFilters.FAVORITES]);
};

/**
 * The favorites pill works together with the pills that can narrow saved items (`FAVORITES_COMPATIBLE_FILTERS`)
 * and excludes every other pill: selecting it deselects them, and selecting one of them deselects it.
 * Deselections, including a dropdown set back to "All", pass through.
 */
export const withFavoritesExclusivity = (changedFilterID: string, changedSubFilterID: string | null, selectedFilters: SelectedFilter[]): SelectedFilter[] => {
  if (isFilterDeselection(changedSubFilterID)) {
    return selectedFilters;
  }
  if (changedFilterID === StockButtonFilters.FAVORITES.valueOf()) {
    return withoutFiltersIncompatibleWithFavorites(selectedFilters);
  }
  if (isFavoritesCompatibleFilter(changedFilterID)) {
    return selectedFilters;
  }
  return withoutFavoritesFilter(selectedFilters);
};

/** Favorites have no search, so a search turns the favorites pill off, and nothing turns it back on. */
export const withoutFavoritesFilter = (selectedFilters: SelectedFilter[]): SelectedFilter[] => {
  return selectedFilters.filter((selectedFilter) => !isFavoritesFilter(selectedFilter));
};

/** While the favorites pill is on, the pills it excludes stay off, even the seasonal pill a cleared search turns back on. */
export const withoutFiltersExcludedByFavorites = (selectedFilters: SelectedFilter[]): SelectedFilter[] => {
  return isFavoritesFilterSelected(selectedFilters) ? withoutFiltersIncompatibleWithFavorites(selectedFilters) : selectedFilters;
};

/** Whether a pill beside the favorites pill narrows the saved items, so an empty view means "no match" rather than "nothing saved". */
export const isFavoritesViewNarrowed = (selectedFilters: SelectedFilter[]): boolean => {
  return isAnySubFilterSelected(selectedFilters, FAVORITES_COMPATIBLE_FILTERS);
};

const isFavoritesFilter = (selectedFilter: SelectedFilter): boolean => {
  return selectedFilter.filter === StockButtonFilters.FAVORITES.valueOf();
};

const isFavoritesCompatibleFilter = (filterID: string): boolean => {
  return FAVORITES_COMPATIBLE_FILTERS.includes(filterID);
};

const withoutFiltersIncompatibleWithFavorites = (selectedFilters: SelectedFilter[]): SelectedFilter[] => {
  return selectedFilters.filter((selectedFilter) => isFavoritesFilter(selectedFilter) || isFavoritesCompatibleFilter(selectedFilter.filter));
};

const isFilterDeselection = (changedSubFilterID: string | null): boolean => {
  return changedSubFilterID === null || changedSubFilterID === DEFAULT_APPLIED_SUBFILTER.id;
};

/** The season the user has left switched on for this request, or null when the pill is off. */
export const getSelectedStockSeason = (selectedFilters: SelectedFilter[]): SeasonName | null => {
  const season = getActiveStockSeason();
  if (season === null) {
    return null;
  }

  const isPillOn = selectedFilters.some((selectedFilter) => {
    return selectedFilter.filter === StockButtonFilters.SEASONAL.valueOf() && selectedFilter.subFilter === season;
  });

  return isPillOn ? season : null;
};

const uploadClipartGridItem = async (gridItem: GridClipartItemStorage): Promise<ImageData> => {
  const {source} = gridItem;
  let data: UserImageVOResponse;

  switch (source) {
    case SOURCE.PIXABAY_ILLUSTRATIONS:
      data = await runUserImageUploadJob(() =>
        pixabayImageUploadV2({
          url: gridItem.data.hu as string,
          ...(gridItem.data.id ? {imageId: String(gridItem.data.id)} : {}),
        })
      );
      break;

    case SOURCE.STORYBLOCKS_ILLUSTRATIONS:
      data = await runUserImageUploadJob(() =>
        storyBlocksImageUploadV2({
          imageId: gridItem.data.id as string,
        })
      );
      break;

    case SOURCE.GETTY_ILLUSTRATIONS:
      data = await runUserImageUploadJob(() =>
        gettyImageUploadV2({
          imageId: gridItem.data.id as string,
          type: gridItem.data.type as string,
          title: gridItem.data.title as string,
          artist: gridItem.data.artist as string,
          collection: gridItem.data.col as number,
        })
      );
      break;

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return getImageDataFromUserImageVO(data);
};

export const getHasUserSeenMobileVideoPreview = (): Promise<boolean> => {
  return window.PMW.readLocal('stockmedia/hasUserSeenMobileVideoPreviewMessage');
};

export const markUserHasSeenMobileVideoPreview = (): Promise<boolean> => {
  return window.PMW.readLocal('stockmedia/markUserHasSeenMobileVideoPreviewMessage');
};

export const getSearchBarPlaceholderText = (selectedTab?: StockPanelTabs): string => {
  if (selectedTab) {
    switch (selectedTab) {
      case StockPanelTabs.PHOTOS:
        return window.i18next.t('pmwjs_search_photos');
      case StockPanelTabs.VIDEOS:
        return window.i18next.t('pmwjs_search_videos');
      case StockPanelTabs.SHAPES:
        return window.i18next.t('pmwjs_search_shapes');
      case StockPanelTabs.STICKERS:
        return window.i18next.t('pmwjs_search_stickers');
      case StockPanelTabs.ICONS:
        return window.i18next.t('pmwjs_search_icons');
      case StockPanelTabs.AUDIOS:
        return window.i18next.t('pmwjs_search_audios');
      case StockPanelTabs.TEXT_TO_IMAGE:
        return window.i18next.t('pmwjs_prompt');
      case StockPanelTabs.FAVORITES:
        // A search from the Favorites tab runs in the All tab, so it shows the All tab's placeholder.
        return getSearchBarPlaceholderText();
      default:
        throw new Error(`Unhandled stock tab: ${selectedTab}`);
    }
  }
  return window.i18next.t('pmwjs_explore_photos_videos_and_more');
};

export const DEFAULT_TABS = [
  StockPanelTabs.ALL,
  StockPanelTabs.STICKERS,
  StockPanelTabs.SHAPES,
  StockPanelTabs.PHOTOS,
  StockPanelTabs.VIDEOS,
  StockPanelTabs.ICONS,
  StockPanelTabs.AUDIOS,
];

// Favorites are per user, so only a logged-in user's tab bar ends with the Favorites tab.
const LOGGED_IN_USER_TABS = [...DEFAULT_TABS, StockPanelTabs.FAVORITES];

export const getStockTabsOrder = (): StockPanelTabs[] => {
  return isUserLoggedIn() ? LOGGED_IN_USER_TABS : DEFAULT_TABS;
};

/**
 * Orders the carousel rows for whichever view the panel is showing: a search leads with the internal
 * types and trails the externally sourced ones, a browse keeps the default order except in-season.
 * A single visible tab has nothing to reorder for a search, so it browses.
 */
export const getCarouselTabOrder = (tabsToShow: StockPanelTabs[], searchTerm: string | undefined): StockPanelTabs[] => {
  const isSearching = Boolean(searchTerm) && tabsToShow.length > 1;
  return isSearching ? getSearchTabOrder(tabsToShow) : getSeasonalBrowseTabOrder(tabsToShow);
};

// During a search the internal PMW types (OpenSearch-backed) lead, in this fixed order; the externally
// sourced types (Getty / Storyblocks) are pushed to the end. See plans/phase-28.md.
const SEARCH_PRIORITY_TABS = [StockPanelTabs.SHAPES, StockPanelTabs.STICKERS, StockPanelTabs.ICONS];
const SEARCH_TRAILING_TABS = [StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS, StockPanelTabs.AUDIOS];

/**
 * Orders the carousels shown while a search is active: internal priority tabs first (Shapes, Stickers,
 * Icons), then any tab not in either group, then the externally sourced tabs (Photos, Videos, Audios).
 * Only tabs present in `tabsToShow` are kept, so it is safe to pass a custom subset.
 */
export const getSearchTabOrder = (tabsToShow: StockPanelTabs[]): StockPanelTabs[] => {
  const grouped = new Set([...SEARCH_PRIORITY_TABS, ...SEARCH_TRAILING_TABS]);
  const present = (tabs: StockPanelTabs[]): StockPanelTabs[] => tabs.filter((tab) => tabsToShow.includes(tab));
  const ungrouped = tabsToShow.filter((tab) => !grouped.has(tab));
  return [...present(SEARCH_PRIORITY_TABS), ...ungrouped, ...present(SEARCH_TRAILING_TABS)];
};

// Shapes and Icons have no season-specific content, so the in-season browse demotes their
// carousels to the bottom and lets the season-boosted rows lead. See plans/seasonal-stock.md.
const SEASONAL_BROWSE_TRAILING_TABS = new Set([StockPanelTabs.SHAPES, StockPanelTabs.ICONS]);

/**
 * Orders the browse (no-search) carousels while a season is active: every tab keeps its
 * default order except the seasonal trailing tabs (Shapes, Icons), which move to the end.
 * Outside a season — or with seasonal stock switched off, when nothing is ranked seasonally to
 * justify the demotion — the input order is returned unchanged. The tab bar is never reordered.
 */
export const getSeasonalBrowseTabOrder = (tabsToShow: StockPanelTabs[]): StockPanelTabs[] => {
  if (getActiveStockSeason() === null) {
    return tabsToShow;
  }

  return [...tabsToShow.filter((tab) => !SEASONAL_BROWSE_TRAILING_TABS.has(tab)), ...tabsToShow.filter((tab) => SEASONAL_BROWSE_TRAILING_TABS.has(tab))];
};

export const shouldShowShowMoreCTA = (carouselType: StockCarouselType): boolean => {
  switch (carouselType) {
    case StockCarouselType.RECENTS:
      return false;
    case StockCarouselType.AI:
    case StockCarouselType.ICONS:
    case StockCarouselType.AUDIOS:
    case StockCarouselType.PHOTOS:
    case StockCarouselType.SHAPES:
    case StockCarouselType.STICKERS:
    case StockCarouselType.VIDEOS:
      return true;
    default:
      throw new Error(`Unhanled carousel type: ${carouselType}`);
  }
};

export const getTestIdForCarouselType = (carouselType: StockCarouselType): PosterEditorTestId | undefined => {
  switch (carouselType) {
    case StockCarouselType.SHAPES:
      return PosterEditorTestId.STOCK_PANEL_CAROUSEL_SHAPES;
    default:
      return undefined;
  }
};

export const getCarouselTitleText = (carouselType: StockCarouselType): string => {
  switch (carouselType) {
    case StockCarouselType.RECENTS:
      return window.i18next.t('pmwjs_recent');
    case StockCarouselType.PHOTOS:
      return window.i18next.t('pmwjs_photos_2');
    case StockCarouselType.STICKERS:
      return window.i18next.t('pmwjs_stickers');
    case StockCarouselType.SHAPES:
      return window.i18next.t('pmwjs_shapes');
    case StockCarouselType.VIDEOS:
      return window.i18next.t('pmwjs_videos_2');
    case StockCarouselType.AUDIOS:
      return window.i18next.t('pmwjs_audios_2');
    case StockCarouselType.ICONS:
      return window.i18next.t('pmwjs_icons');
    case StockCarouselType.AI:
      return window.i18next.t('pmwjs_ai');
    default:
      throw new Error(`Unhandled carousel type for getCarouselTitleText: ${carouselType}`);
  }
};

export const getBottomPills = (selectedTab: string, callback: (id: string) => void): PanelBottomPill[] => {
  return [
    {
      id: StockPanelTabs.PHOTOS,
      text: window.i18next.t('pmwjs_photos_2'),
      icon: 'icon-image',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.PHOTOS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.TEXT_TO_IMAGE,
      text: window.i18next.t('pmwjs_ai_images'),
      icon: 'icon-ai-image',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.TEXT_TO_IMAGE,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.SHAPES,
      text: window.i18next.t('pmwjs_shapes'),
      icon: 'icon-elements',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.SHAPES,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.STICKERS,
      text: window.i18next.t('pmwjs_stickers'),
      icon: 'icon-sticker',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.STICKERS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.ICONS,
      text: window.i18next.t('pmwjs_icons'),
      icon: 'icon-elements',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.ICONS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.VIDEOS,
      text: window.i18next.t('pmwjs_videos_2'),
      icon: 'icon-video',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.VIDEOS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.AUDIOS,
      text: window.i18next.t('pmwjs_audios_2'),
      icon: 'icon-audio',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.AUDIOS,
      onClick(id: string): void {
        callback(id);
      },
    },
  ];
};

export const getOptionsForStockGetter = (selectedFilters: SelectedFilter[], selectedTab: string, audioFilterIDs: (StockButtonFilters | string)[]): StockGetterOptions => {
  const stockGetterParam: StockGetterOptions = {};
  if (selectedFilters.length > 0) {
    if ((selectedTab as StockPanelTabs) === StockPanelTabs.AUDIOS) {
      const audioFilters: Record<StockButtonFilters, string[]> | Record<string, string[]> = {};
      selectedFilters.forEach((selectedFilter) => {
        const isPresent = audioFilterIDs.includes(selectedFilter.filter);
        if (isPresent && selectedFilter.subFilter) {
          audioFilters[selectedFilter.filter as StockButtonFilters] = [selectedFilter.subFilter];
        }
      });
      stockGetterParam.audioFilters = audioFilters;
    } else if ((selectedTab as StockPanelTabs) === StockPanelTabs.VIDEOS) {
      stockGetterParam.only4KVideos = selectedFilters.some((filters) => {
        return (filters.filter as StockButtonFilters) === StockButtonFilters.QUALITY && filters.subFilter === UHD_APPLIED_VIDEO_FILTER_ID;
      })
        ? true
        : undefined;
    } else if ((selectedTab as StockPanelTabs) === StockPanelTabs.ICONS) {
      const categorySubFilter = getSelectedCategorySubFilter(selectedFilters);
      if (categorySubFilter) {
        stockGetterParam.iconCategories = ICON_FILTERS_TO_CATEGORY_MAPPING[categorySubFilter];
      }
    } else if ((selectedTab as StockPanelTabs) === StockPanelTabs.SHAPES) {
      const categorySubFilter = getSelectedCategorySubFilter(selectedFilters);
      if (categorySubFilter) {
        stockGetterParam.shapeCategories = SHAPE_FILTERS_TO_CATEGORY_MAPPING[categorySubFilter];
      }
    }
    const orientationFilter = selectedFilters.find((filter) => filter.filter === StockButtonFilters.ORIENTATION.valueOf());
    if (orientationFilter?.subFilter) {
      stockGetterParam.orientation = orientationFilter.subFilter;
    }
  }
  return stockGetterParam;
};

/** Only the Category pill picks categories: the favorites pill can be selected beside it. */
const getSelectedCategorySubFilter = (selectedFilters: SelectedFilter[]): string | null => {
  return selectedFilters.find((selectedFilter) => selectedFilter.filter === StockButtonFilters.CATEGORY.valueOf())?.subFilter ?? null;
};

export const getCarouselDataForShapeItem = (shapeData: PMWStockBackendShape): CarouselItemData => {
  return {
    gridItemData: shapeDataToShapeItem(shapeData),
    height: FALLBACK_GRID_ITEM_HEIGHT,
    width: FALLBACK_GRID_ITEM_WIDTH,
  };
};

export const getCarouselDataForIconItem = (iconData: PMWStockBackendIcon): CarouselItemData => {
  return {
    gridItemData: iconDataToIconItem({...iconData, sortOrder: iconData.adminScore, isHidden: iconData.isHidden !== 1}),
    height: FALLBACK_GRID_ITEM_HEIGHT,
    width: FALLBACK_GRID_ITEM_WIDTH,
  };
};

export const getCarouselDataForAiIconItem = (aiIconData: PMWStockBackendAiIcon): CarouselItemData => {
  return {
    gridItemData: aiIconDataToAiIconItem({...aiIconData, sortOrder: aiIconData.adminScore, isHidden: aiIconData.isHidden !== 1}),
    height: FALLBACK_GRID_ITEM_HEIGHT,
    width: FALLBACK_GRID_ITEM_WIDTH,
  };
};

export const getCarouselDataForStillStickerItem = (stillStickerData: PMWStockBackendStillSticker): CarouselItemData => {
  return {
    gridItemData: stillStickerDataToStickerItem({...stillStickerData, sortOrder: stillStickerData.adminScore, isHidden: stillStickerData.isHidden !== 1}),
    height: stillStickerData.height,
    width: stillStickerData.width,
  };
};

export const getCarouselDataForAiStickerItem = (aiStickerData: PMWStockBackendAiSticker): CarouselItemData => {
  return {
    gridItemData: aiStickerDataToAiStickerItem({...aiStickerData, sortOrder: aiStickerData.adminScore, isHidden: aiStickerData.isHidden !== 1}),
    height: aiStickerData.height,
    width: aiStickerData.width,
  };
};

export const getCarouselDataForExtractedGettyStickerItem = (extractedGettyStickerData: PMWStockBackendExtractedGettySticker): CarouselItemData => {
  return {
    gridItemData: extractedGettyStickerDataToStickerItem({
      ...extractedGettyStickerData,
      sortOrder: extractedGettyStickerData.adminScore,
      isHidden: extractedGettyStickerData.isHidden !== 1,
    }),
    height: extractedGettyStickerData.height,
    width: extractedGettyStickerData.width,
  };
};

export const getCarouselDataForStickerItem = (stickerData: PMWStockBackendAnimatedSticker): CarouselItemData => {
  return {
    gridItemData: animatedStickerDataToAnimatedStickerItem(stickerData),
    height: stickerData.height,
    width: stickerData.width,
  };
};

export const getCarouselDataForAiAnimatedStickerItem = (stickerData: PMWStockBackendAiAnimatedSticker): CarouselItemData => {
  return {
    gridItemData: aiAnimatedStickerDataToAiAnimatedStickerItem(stickerData),
    height: stickerData.height,
    width: stickerData.width,
  };
};

export const getCarouselDataForAudioItem = (audioData: GridStockAudioItemBackEndData, height: number): CarouselItemData => {
  return {
    gridItemData: {
      ...audioDataToAudioItem(audioData),
      gridItemHeight: height,
    } as GridStockAudioItemStorage,
    width: STOCK_AUDIO_GRID_ITEM_WIDTH,
    height,
    hasFixedWidth: true,
  };
};

/**
 * A recent item is stored as the grid item it was added from. An audio card keeps the fixed card width the
 * Audio row gives it, so the carousel's aspect cap cannot squeeze it; every other tile scales by its aspect.
 */
export const getCarouselDataForRecentStockItem = (recentItem: GridItemBaseStorageBackendData): CarouselItemData => {
  const gridItemData = getGridItemForBackendData(recentItem);
  if (recentItem.type === GRID_ITEM_TYPE.STOCK_AUDIO) {
    return {
      gridItemData: {...gridItemData, gridItemHeight: CAROUSEL_ITEM_HEIGHT},
      width: STOCK_AUDIO_GRID_ITEM_WIDTH,
      height: CAROUSEL_ITEM_HEIGHT,
      hasFixedWidth: true,
    };
  }
  return {
    gridItemData,
    width: Number(recentItem.width ?? FALLBACK_GRID_ITEM_WIDTH),
    height: Number(recentItem.height ?? FALLBACK_GRID_ITEM_HEIGHT),
  };
};

export const getCarouselDataForImageItem = (imageData: GridStockImageItemBackEndData, source: StockSources): CarouselItemData => {
  return {
    gridItemData: imageDataToImageItem(imageData, source),
    width: Number(imageData.w),
    height: Number(imageData.h),
  };
};

export const getCarouselDataForClipartItem = (clipartData: GridClipartItemBackEndData, source: StockSources): CarouselItemData => {
  return {
    gridItemData: clipartDataToClipartItem(clipartData, source),
    width: Number(clipartData.w) || FALLBACK_GRID_ITEM_WIDTH,
    height: Number(clipartData.h) || FALLBACK_GRID_ITEM_HEIGHT,
  };
};

export const getCarouselDataForVideoItem = (videoData: GridStockVideoItemBackEndData, source: StockSources): CarouselItemData | undefined => {
  const gridItem = videoDataToVideoItem(videoData, source);

  if (gridItem) {
    return {
      gridItemData: gridItem,
      width: Number(videoData.w),
      height: Number(videoData.h),
    };
  }

  return undefined;
};

export const getGridID = (tab: StockPanelTabs): StockGridIDs => {
  switch (tab) {
    case StockPanelTabs.PHOTOS:
      return StockGridIDs.PHOTOS;
    case StockPanelTabs.VIDEOS:
      return StockGridIDs.VIDEOS;
    case StockPanelTabs.SHAPES:
      return StockGridIDs.SHAPES;
    case StockPanelTabs.STICKERS:
      return StockGridIDs.STICKERS;
    case StockPanelTabs.ICONS:
      return StockGridIDs.ICONS;
    case StockPanelTabs.AUDIOS:
      return StockGridIDs.AUDIOS;
    case StockPanelTabs.TEXT_TO_IMAGE:
      return StockGridIDs.TEXT_TO_IMAGE;
    case StockPanelTabs.FAVORITES:
      return StockGridIDs.FAVORITES;
    default:
      throw new Error(`Unhandled selected tab: ${tab} for getting grid id`);
  }
};

/**
 * The grid id of a favorites view. The Favorites tab keeps its own id. A media tab's favorites pill lists other items than the
 * tab does, and each query of it other items again, so its id names the query: a change of pills mounts a new grid that pages from the top.
 */
export const getFavoritesGridId = (selectedTab: StockPanelTabs, tabGridId: string, favoritesQueryKey: string): string => {
  if (selectedTab === StockPanelTabs.FAVORITES) {
    return tabGridId;
  }
  return `${tabGridId}-favorites-${favoritesQueryKey}`;
};

/** The tiles whose id the grid has not shown yet. An id repeated within the batch counts once, at its first place. */
export const getTilesNotYetShown = (tiles: GridItemStorage[], shownIds: ReadonlySet<string>): GridItemStorage[] => {
  const newTilesById = new Map<string, GridItemStorage>();
  tiles.forEach((tile) => {
    if (!shownIds.has(tile.id) && !newTilesById.has(tile.id)) {
      newTilesById.set(tile.id, tile);
    }
  });
  return [...newTilesById.values()];
};

export const getTabItem = (tab: StockPanelTabs, textClasses?: string): TabItemContentStorage => {
  return {
    tabKey: tab,
    text: getTabDisplayText(tab),
    textClassName: textClasses,
  };
};

const getTabDisplayText = (tab: StockPanelTabs): string => {
  switch (tab) {
    case StockPanelTabs.PHOTOS:
      return window.i18next.t('pmwjs_photos_2');
    case StockPanelTabs.SHAPES:
      return window.i18next.t('pmwjs_shapes');
    case StockPanelTabs.STICKERS:
      return window.i18next.t('pmwjs_stickers');
    case StockPanelTabs.ICONS:
      return window.i18next.t('pmwjs_icons');
    case StockPanelTabs.VIDEOS:
      return window.i18next.t('pmwjs_videos_2');
    case StockPanelTabs.AUDIOS:
      return window.i18next.t('pmwjs_audios_2');
    case StockPanelTabs.TEXT_TO_IMAGE:
      return window.i18next.t('pmwjs_ai_images');
    case StockPanelTabs.FAVORITES:
      return window.i18next.t('pmwjs_favorites');
    default:
      throw new Error(`Unhandled tab type for getTabDisplayText: ${tab}`);
  }
};

/**
 * The favorites empty-state copy. With a pill selected beside the favorites pill, it says that no favorite matches the
 * pills; with no other pill, that nothing is saved yet, naming the tab's own media. The Favorites tab, which spans every
 * type and has no pills, gets the generic line.
 */
export const getFavoritesEmptyMessage = (tab: StockPanelTabs, selectedFilters: SelectedFilter[]): string => {
  return window.i18next.t(isFavoritesViewNarrowed(selectedFilters) ? 'pmwjs_favorites_no_filter_matches' : FAVORITES_EMPTY_MESSAGE_KEY_BY_TAB[tab]);
};

export const getStockFilterForAudioFilterBackendData = (backendData: AudioCategoryBackendData): StockFilter => {
  return {
    id: backendData.id as StockFilterID,
    type: StockFilterTypes.DROPDOWN,
    name: AUDIO_CATEGORY_ID_TO_STOCK_BUTTON_FILTER[backendData.id],
    subFilters: backendData.subcategories.map((subcategoryData) => {
      return {
        id: `${subcategoryData.id}`,
        text: subcategoryData.displayName,
      };
    }),
  };
};

export const getSelectedSubfilterIDForStockFilter = (stockFilter: StockFilter, selectedFilters: SelectedFilter[]): string | undefined => {
  if (selectedFilters.length > 0) {
    const selectedFilter = selectedFilters.find((filterSelected) => {
      return (filterSelected.filter as StockFilterID) === stockFilter.id;
    });
    if (selectedFilter) {
      const selectedSubfilter = stockFilter.subFilters.find((subfilter) => {
        return subfilter.id === selectedFilter.subFilter;
      });
      return selectedSubfilter?.id;
    }
  }
  return undefined;
};

const doesSubFilterRefMatch = (ref: StockSubFilterRef, filterID: string, subFilterID: string | null): boolean => {
  return ref.filter.valueOf() === filterID && ref.subFilter === subFilterID;
};

const getSubFiltersExclusiveWith = (tab: StockPanelTabs, filterID: string, subFilterID: string): StockSubFilterRef[] => {
  const pairs = MUTUALLY_EXCLUSIVE_SUBFILTERS[tab] ?? [];
  return pairs
    .filter(([first, second]) => {
      return doesSubFilterRefMatch(first, filterID, subFilterID) || doesSubFilterRefMatch(second, filterID, subFilterID);
    })
    .map(([first, second]) => {
      return doesSubFilterRefMatch(first, filterID, subFilterID) ? second : first;
    });
};

export const removeSubFiltersExclusiveWith = (tab: StockPanelTabs, filterID: string, subFilterID: string, selectedFilters: SelectedFilter[]): SelectedFilter[] => {
  const exclusiveRefs = getSubFiltersExclusiveWith(tab, filterID, subFilterID);
  return selectedFilters.filter((selected) => {
    return !exclusiveRefs.some((ref) => {
      return doesSubFilterRefMatch(ref, selected.filter, selected.subFilter);
    });
  });
};

const getTagForMediaItem = (source: SOURCE): Partial<GridMediaItemTag> | undefined => {
  switch (source) {
    case SOURCE.GETTY_ILLUSTRATIONS:
    case SOURCE.GETTY:
      return getTagForGettyMedia();
    default:
      return undefined;
  }
};

const gridAudioItemBackendDataToAudioItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStockAudioItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.STOCK_AUDIO,
    name: gridItemBackendData.name as string,
    data: gridItemBackendData.data as Record<string, any>,
    id: gridItemBackendData.id as string,
  };
};

const gridShapeItemBackendDataToShapeItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridShapeItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_SHAPE,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridClipartItemBackendDataToClipartItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridClipartItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.CLIPART,
    thumbSrc: gridItemBackendData.thumbSrc as string,
    source: gridItemBackendData.source as SOURCE,
    id: gridItemBackendData.id as string,
    data: gridItemBackendData.data as Record<string, any>,
    tag: getTagForMediaItem(gridItemBackendData.source as SOURCE),
  };
};

const gridStockVideoItemBackendDataToStockVideoItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStockVideoItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.STOCK_VIDEO,
    source: gridItemBackendData.source as string,
    data: gridItemBackendData.data as Record<string, any>,
    id: gridItemBackendData.id as string,
    isUHD: !!gridItemBackendData.isUHD,
    tag: getTagForMediaItem(gridItemBackendData.source as SOURCE),
  };
};

const gridAnimatedStickerItemBackendDataToAnimatedStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAnimatedStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    duration: Number(gridItemBackendData.duration),
    frameRate: Number(gridItemBackendData.frameRate),
    id: gridItemBackendData.id as string,
    highResAnimatedSprite: gridItemBackendData.highResAnimatedSprite as AnimatedSprite,
    screenAnimatedSprite: gridItemBackendData.screenAnimatedSprite as AnimatedSprite,
  };
};

const gridAiAnimatedStickerItemBackendDataToAiAnimatedStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAnimatedStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    duration: Number(gridItemBackendData.duration),
    frameRate: Number(gridItemBackendData.frameRate),
    id: gridItemBackendData.id as string,
    highResAnimatedSprite: gridItemBackendData.highResAnimatedSprite as AnimatedSprite,
    screenAnimatedSprite: gridItemBackendData.screenAnimatedSprite as AnimatedSprite,
  };
};

const gridStockImageItemBackendDataToStockImageItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStockImageItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    thumbSrc: gridItemBackendData.thumbSrc as string,
    source: gridItemBackendData.source as SOURCE,
    id: gridItemBackendData.id as string,
    data: gridItemBackendData.data as Record<string, any>,
    tag: getTagForMediaItem(gridItemBackendData.source as SOURCE),
  };
};

const gridIconItemBackendDataToStockIconItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridIconItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_ICON,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridAiIconItemBackendDataToStockAiIconItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAiIconItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_AI_ICON,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridStillStickerItemBackendDataToStockStillStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStillStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridAiStickerItemBackendDataToStockAiStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAiStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_AI_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridExtractedGettyStickerItemBackendDataToStockExtractedGettyStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridExtractedGettyStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
    tag: getTagForGettyMedia(),
  };
};

export const getGridItemForBackendData = (gridItemBackendData: GridItemBaseStorageBackendData): GridItemBaseStorage => {
  switch (gridItemBackendData.type as GRID_ITEM_TYPE) {
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return gridAudioItemBackendDataToAudioItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
    case GRID_ITEM_TYPE.SHAPE:
      return gridShapeItemBackendDataToShapeItem(gridItemBackendData);
    case GRID_ITEM_TYPE.CLIPART:
      return gridClipartItemBackendDataToClipartItem(gridItemBackendData);
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return gridStockVideoItemBackendDataToStockVideoItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
    case GRID_ITEM_TYPE.ANIMATED_STICKER:
      return gridAnimatedStickerItemBackendDataToAnimatedStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return gridAiAnimatedStickerItemBackendDataToAiAnimatedStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return gridStockImageItemBackendDataToStockImageItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_AI_ICON:
      return gridAiIconItemBackendDataToStockAiIconItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
    case GRID_ITEM_TYPE.ICON:
      return gridIconItemBackendDataToStockIconItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return gridAiStickerItemBackendDataToStockAiStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
    case GRID_ITEM_TYPE.STILL_STICKER:
      return gridStillStickerItemBackendDataToStockStillStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER:
      return gridExtractedGettyStickerItemBackendDataToStockExtractedGettyStickerItem(gridItemBackendData);
    default:
      throw new Error(`Unhandled type: ${gridItemBackendData.type as string}`);
  }
};


export const trackAiToolMenuOpenFromMediaDrawer = (key: AiPanelKey | null): void => {
  if (key === 'ai-images') {
    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.MEDIA_DRAWER});
  } else if (key === 'ai-videos') {
    trackPosterBuilderGA4Events(GA4EventName.AI_VIDEO_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.MEDIA_DRAWER});
  } else if (key === 'ai-voice') {
    trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.MEDIA_DRAWER});
  }
};

export const getGATrackingSourceFromMediaSource = (src: SOURCE): string => {
  switch (src) {
    case SOURCE.GETTY:
    case SOURCE.GETTY_ILLUSTRATIONS:
      return capitalize(SOURCE.GETTY);
    case SOURCE.STORYBLOCKS:
    case SOURCE.STORYBLOCKS_ILLUSTRATIONS:
      return capitalize(SOURCE.STORYBLOCKS);
    case SOURCE.PIXABAY:
    case SOURCE.PIXABAY_ILLUSTRATIONS:
      return capitalize(SOURCE.PIXABAY);
    case SOURCE.PMW_IMAGE:
      return capitalize(SOURCE.PMW_IMAGE);
    default:
      return SOURCE.PMW.toUpperCase();
  }
};

export const getGATrackingTypeFromMediaType = (gridItemType: GRID_ITEM_TYPE): string => {
  switch (gridItemType) {
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return 'Photo';
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return 'Video';
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return 'Audio';
    case GRID_ITEM_TYPE.CLIPART:
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
      return 'StillSticker';
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return 'AiStillSticker';
    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
      return 'AnimatedSticker';
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return 'AiAnimatedSticker';
    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
      return 'Shape';
    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
      return 'Icon';
    default:
      return '';
  }
};

export const getGATrackingSearchTerm = (tab: StockPanelTabs): string => {
  switch (tab) {
    case StockPanelTabs.ICONS:
      return 'Icon';
    case StockPanelTabs.PHOTOS:
      return 'Photo';
    case StockPanelTabs.VIDEOS:
      return 'Video';
    case StockPanelTabs.AUDIOS:
      return 'Audio';
    case StockPanelTabs.SHAPES:
      return 'Shape';
    case StockPanelTabs.STICKERS:
      return 'Sticker';
    default:
      return capitalize(tab);
  }
};

export const getGATrackingFilterTerm = (filters: SelectedFilter[]): string => {
  for (const filter of filters) {
    if (filter.filter === StockButtonFilters.ANIMATION.valueOf() && filter.subFilter === AnimationSubFilter.ANIMATED) {
      return 'PMWAnimated';
    }
    if (filter.filter === StockButtonFilters.ANIMATION.valueOf() && filter.subFilter === AnimationSubFilter.STILL) {
      return 'Still';
    }
  }
  return '';
};

const shouldShowHalloweenPlaceholder = (): boolean => {
  const month = new Date().getMonth();
  return month === 8 || month === 9;
};

export const getPlaceholderTextForAiImagePrompt = (): string => {
  const AI_IMAGES_WHIMSICAL_TEXTS = [
    window.i18next.t('pmwjs_ai_images_whimsical_1'),
    window.i18next.t('pmwjs_ai_images_whimsical_2'),
    window.i18next.t('pmwjs_ai_images_whimsical_3'),
    window.i18next.t('pmwjs_ai_images_whimsical_4'),
    window.i18next.t('pmwjs_ai_images_whimsical_5'),
  ];

  const AI_IMAGES_NON_WHIMSICAL_TEXTS = [
    window.i18next.t('pmwjs_ai_images_non_whimsical_1'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_2'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_3'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_4'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_5'),
  ];

  const AI_IMAGES_HALLOWEEN_TEXTS = [
    window.i18next.t('pmwjs_ai_images_halloween_1'),
    window.i18next.t('pmwjs_ai_images_halloween_2'),
    window.i18next.t('pmwjs_ai_images_halloween_3'),
    window.i18next.t('pmwjs_ai_images_halloween_4'),
    window.i18next.t('pmwjs_ai_images_halloween_5'),
  ];

  let index = Math.floor(Math.random() * (AI_IMAGES_NON_WHIMSICAL_TEXTS.length + AI_IMAGES_WHIMSICAL_TEXTS.length));
  if (shouldShowHalloweenPlaceholder()) {
    index = Math.floor(Math.random() * AI_IMAGES_HALLOWEEN_TEXTS.length);
    return AI_IMAGES_HALLOWEEN_TEXTS[index];
  }
  return [...AI_IMAGES_NON_WHIMSICAL_TEXTS, ...AI_IMAGES_WHIMSICAL_TEXTS][index];
};

/**
 * Reads the AI-carousel routing marker off a grid item. Items added through the aggregated "AI" carousel carry the
 * type of AI content they represent and the originating generated-asset id, so the add path can route a video to
 * uploadGeneratedAiVideo and a voice to addGeneratedVoiceToDesign instead of the generic stock upload.
 * `aiContentType` / `aiId` live on the storage's open index signature, so they read back as `unknown` and are validated here.
 */
export const getAiCarouselItemMeta = (item: GridItemStorage): AiCarouselItemMeta | undefined => {
  const aiId: unknown = item.aiId;
  if (typeof aiId !== 'number') {
    return undefined;
  }
  const aiContentType: unknown = item.aiContentType;
  switch (aiContentType) {
    case AiCarouselContentType.IMAGES:
      return {aiContentType: AiCarouselContentType.IMAGES, aiId};
    case AiCarouselContentType.VIDEOS:
      return {aiContentType: AiCarouselContentType.VIDEOS, aiId};
    case AiCarouselContentType.AUDIO:
      return {aiContentType: AiCarouselContentType.AUDIO, aiId};
    default:
      return undefined;
  }
};

const AUDIO_GRID_ITEM_TYPES: ReadonlySet<GRID_ITEM_TYPE> = new Set([GRID_ITEM_TYPE.STOCK_AUDIO, GRID_ITEM_TYPE.USER_AUDIO, GRID_ITEM_TYPE.AUDIO]);

/**
 * True for any stock-panel item that adds audio: a stock/user audio tile, or an AI-voice carousel item (a CUSTOM item
 * tagged with the AUDIO ai-content type). Used to decide whether a drag-drop onto the canvas should open the timeline.
 */
export const isAudioStockItem = (item: GridItemStorage): boolean => {
  return AUDIO_GRID_ITEM_TYPES.has(item.type) || getAiCarouselItemMeta(item)?.aiContentType === AiCarouselContentType.AUDIO;
};

/**
 * On desktop, adding an audio item closes the stock popup so the timeline (opened by the
 * audio add itself in AudioClips.addAudioItemsData) is visible. True only on desktop for an
 * audio / AI-voice add — mobile keeps its own timeline-modal + selection-footer flow untouched.
 */
export const isDesktopAudioAdd = (items: GridItemStorage[]): boolean => {
  return !isMobile && items.some(isAudioStockItem);
};

/**
 * Whether an AI media panel should dismiss itself once an item is added. Mobile's full-screen panel
 * covers the poster, so it has to step aside for the add to be visible at all; desktop leaves the panel
 * up so a run of generations can be added one after another.
 */
export const shouldAiPanelCloseOnItemAdd = (): boolean => {
  return isMobile;
};

const AI_VIDEO_THUMB_IMAGE_EXTENSION = 'jpg';
const AI_VIDEO_THUMB_VIDEO_EXTENSION = 'mp4';

export const getCarouselDataForAiVideoItem = (video: AiVideoBackendData): CarouselItemData => {
  const gridItemData: GridStockVideoItemStorage = {
    type: GRID_ITEM_TYPE.STOCK_VIDEO,
    id: `ai-video-${video.id}`,
    source: SOURCE.AI_TEXT_TO_IMAGE,
    imgThumbSrc: getAiVideoThumbUrl(video.hashedFilename, AI_VIDEO_THUMB_IMAGE_EXTENSION),
    videoThumbSrc: getAiVideoThumbUrl(video.hashedFilename, AI_VIDEO_THUMB_VIDEO_EXTENSION),
    isUHD: false,
    duration: `${video.videoProps.duration}`,
    data: {id: video.id},
    aiContentType: AiCarouselContentType.VIDEOS,
    aiId: video.id,
    width: video.videoProps.width,
    height: video.videoProps.height,
  };
  return {
    gridItemData,
    width: video.videoProps.width,
    height: video.videoProps.height,
  };
};

/**
 * Uploads an item picked from an AI carousel and returns the element to add, routing by its AI content type: voice
 * adds itself to the design (and shows its own loading) so it returns null; video uploads the generated video;
 * everything else (AI images) goes through the standard stock upload.
 */
export const isAiVoiceGridItem = (item: GridItemStorage): boolean => {
  return getAiCarouselItemMeta(item)?.aiContentType === AiCarouselContentType.AUDIO;
};

export const uploadAiCarouselItem = async (item: GridItemStorage, shouldLoadDataUrlImage = false, dropPosition?: CanvasDropPosition): Promise<ItemData | null> => {
  const meta = getAiCarouselItemMeta(item);
  if (meta?.aiContentType === AiCarouselContentType.AUDIO) {
    await addGeneratedVoiceToDesign(meta.aiId);
    return null;
  }
  const element = meta?.aiContentType === AiCarouselContentType.VIDEOS ? await uploadGeneratedAiVideo(meta.aiId) : await uploadSelectedItem(item, shouldLoadDataUrlImage);
  return placeItemAtDropPosition(element, dropPosition);
};

export const getGridItemForAIGeneratedImageV2 = (aiGeneratedImageBackendData: AITextToImageBackendData): GridAiImageItemStorage => {
  if (isAiEditedImage(aiGeneratedImageBackendData)) {
    return getGridItemForAiEditedImage(aiGeneratedImageBackendData);
  }
  const thumbSizeURL = getUrlForGeneratedAiImage(aiGeneratedImageBackendData.hashedFilename, aiGeneratedImageBackendData.imageProps.ext, MediaItemSizeType.THUMB);

  return {
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    id: aiGeneratedImageBackendData.id,
    thumbSrc: thumbSizeURL,
    highResSrc: getUrlForGeneratedAiImage(aiGeneratedImageBackendData.hashedFilename, aiGeneratedImageBackendData.imageProps.ext, MediaItemSizeType.HIGH_RES),
    source: SOURCE.AI_TEXT_TO_IMAGE,
    width: Number(aiGeneratedImageBackendData.imageProps.width),
    height: Number(aiGeneratedImageBackendData.imageProps.height),
    data: {
      uploaderId: aiGeneratedImageBackendData.uploaderId,
      id: aiGeneratedImageBackendData.id,
    },
    backendData: aiGeneratedImageBackendData,
  };
};

const getGridItemForAiEditedImage = (editedImage: AITextToImageBackendData & {userImage: UserImageVOResponse}): GridAiImageItemStorage => {
  const {userImage} = editedImage;

  return {
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    id: editedImage.id,
    thumbSrc: repoImageThumbURL(userImage.filename, userImage.ext, Boolean(userImage.isUpscaled)),
    highResSrc: repoImageScreenURL(userImage.filename, userImage.ext),
    source: SOURCE.UPLOAD,
    width: editedImage.imageProps.width,
    height: editedImage.imageProps.height,
    data: {
      uploaderId: editedImage.uploaderId,
      id: editedImage.id,
    },
    backendData: editedImage,
  };
};

/** True for an AI image the user generated (or edited with AI): it carries the generation's backend data, unlike stock images and AI videos. */
export const isAiGeneratedImageGridItem = (gridItem: GridItemStorage): gridItem is GridAiImageItemStorage & {backendData: AITextToImageBackendData} => {
  if (gridItem.type !== GRID_ITEM_TYPE.STOCK_IMAGE) {
    return false;
  }
  const backendData: unknown = gridItem.backendData;
  return isAiTextToImageBackendData(backendData);
};

export const isAiEditedImageGridItem = (gridItem: GridItemStorage): gridItem is GridAiImageItemStorage & {backendData: AITextToImageBackendData & {userImage: UserImageVOResponse}} => {
  return isAiGeneratedImageGridItem(gridItem) && isAiEditedImage(gridItem.backendData);
};

export const createKeepFiltersHiddenForTab = (trueTabs: StockPanelTabs[] = []): Record<StockPanelTabs, boolean> => {
  return Object.values(StockPanelTabs).reduce<Record<StockPanelTabs, boolean>>(
    (acc, tab) => {
      acc[tab] = trueTabs.includes(tab);
      return acc;
    },

    {} as Record<StockPanelTabs, boolean>
  );
};

interface SlideshowStockPanelConfig {
  tabsToShow: StockPanelTabs[];
  aiCarouselContentTypes: AiCarouselContentType[];
  blacklistedSources: StockSources[];
  shouldLoadDataUrlImage: boolean;
  addInBulk: boolean;
  enableMultiSelection: boolean;
}

// Single source of truth for the stock panel config shared by every slideshow entry point (new slideshow + add slide, popup +
// inline, desktop + mobile). Slideshow is a visual flow: photos and videos, with the aggregated AI carousel (images + videos)
// rather than the text-to-image carousel. Centralised so these entry points can't drift apart.
export const getSlideshowStockPanelConfig = (): SlideshowStockPanelConfig => ({
  // ALL leads so the panel opens on the carousel browse view; the rest is the carousel row order.
  tabsToShow: [StockPanelTabs.ALL, StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS],
  aiCarouselContentTypes: [AiCarouselContentType.IMAGES, AiCarouselContentType.VIDEOS],
  blacklistedSources: getAnimatedStickerSources(),
  shouldLoadDataUrlImage: true,
  // N picks become one slideshow (or one batch of slides), and prepareAndAddMediaSlides runs its own batch Getty filter — both need the whole selection in a single call.
  addInBulk: true,
  enableMultiSelection: true,
});

/**
 * Pre-fills the AI generator prompt boxes with the user's active stock search term when a Create-with-AI
 * entry point opens an AI panel — the term is just placed in the box, nothing generates. AI Voice is
 * deliberately excluded (its box holds the literal script to speak, not a description of what to make).
 */
export const preFillAiPromptBoxesForAiPanelKey = (key: AiPanelKey, searchTerm: string | undefined): void => {
  if (searchTerm === undefined || searchTerm.trim() === '') {
    return;
  }
  if (key === 'ai-images' || key === 'ai-stickers' || key === 'ai-all') {
    preFillAiPromptBox(AI_IMAGE_PROMPT_BOX_ID, searchTerm);
  }
  if (key === 'ai-videos' || key === 'ai-all') {
    preFillAiPromptBox(AI_VIDEO_PROMPT_BOX_ID, searchTerm);
  }
};

/**
 * Every stock add/upload failure shows the same generic dialog. Kept in one place so the five call
 * sites across the stock and AI panels cannot drift apart in what the user is told.
 */
export const reportStockAddFailure = (error: unknown): void => {
  console.error(error);
  openModal(MODAL_IDS.ERROR_MODAL_ID, {message: window.i18next.t('pmwjs_general_ajax_error'), log: false});
};

/**
 * Hands items to the host, containing any failure to this delivery: one canvas add that throws must never
 * discard the siblings that uploaded successfully.
 */
export const deliverStockItems = async (items: ItemData[], onDeliver: StockItemDelivery): Promise<void> => {
  try {
    await onDeliver(items);
  } catch (e) {
    reportStockAddFailure(e);
  }
};

const getStockAddProgressText = (addedCount: number, totalCount: number): string => {
  return `${window.i18next.t('pmwjs_adding')} ${addedCount} of ${totalCount}`;
};

/**
 * The upload pipeline behind every stock/AI add CTA. AI voice items add themselves to the design inside
 * uploadAiCarouselItem and must not run concurrently (they race each other in the audio-clip domain), so they
 * upload sequentially first; everything else uploads in parallel and — unless the host wants the whole
 * selection in one call — is delivered as soon as its own upload settles, keeping the loading toast's progress
 * text in step. Every failure is contained per item, so one bad upload can never abort or discard its siblings.
 */
export const uploadStockSelection = async ({
  items,
  loadingKey,
  addInBulk,
  onDeliver,
  shouldLoadDataUrlImage = false,
  dropPosition,
  onUploadsStarting,
}: UploadStockSelectionParams): Promise<StockSelectionUploadResult> => {
  const voiceItems = items.filter(isAiVoiceGridItem);
  const itemsToUpload = items.filter((item) => !isAiVoiceGridItem(item));

  showLoading(loadingKey, {text: getStockAddProgressText(1, itemsToUpload.length > 0 ? itemsToUpload.length : items.length)});

  for (const voiceItem of voiceItems) {
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential on purpose, see above
      await uploadAiCarouselItem(voiceItem);
    } catch (e) {
      reportStockAddFailure(e);
    }
  }

  try {
    onUploadsStarting?.(itemsToUpload);
  } catch (e) {
    // A host's bookkeeping (analytics, Redux flags) must not be able to abort the uploads it was announcing.
    reportStockAddFailure(e);
  }

  let settledUploads = 0;
  const uploads = await Promise.all(
    itemsToUpload.map(async (item): Promise<StockSelectionUpload> => {
      let uploadedItem: ItemData | null;
      try {
        uploadedItem = await uploadAiCarouselItem(item, shouldLoadDataUrlImage, dropPosition);
      } catch (e) {
        reportStockAddFailure(e);
        return {item, uploadedItem: null};
      } finally {
        settledUploads += 1;
        updateLoadingText(loadingKey, getStockAddProgressText(Math.min(settledUploads + 1, itemsToUpload.length), itemsToUpload.length));
      }
      if (uploadedItem && !addInBulk) {
        // Streamed delivery: the item lands on the design as soon as its own upload settles. onAddItems serializes the actual canvas adds, so the Getty-limit checks stay ordered and exact.
        await deliverStockItems([uploadedItem], onDeliver);
      }
      return {item, uploadedItem};
    })
  );

  return {
    uploads,
    uploadedItems: uploads.flatMap((upload) => (upload.uploadedItem === null ? [] : [upload.uploadedItem])),
  };
};
